import { useEffect, useMemo, useState } from 'react';
import { DESKTOP_BREAKPOINT, SAFE_BOTTOM, TOKENS } from '../theme/tokens';
import { getTelegramWebApp } from './useTelegramContext';

/**
 * The three surfaces the design covers. The spec's four device *frames* collapse
 * to three here — `Mini App · iPhone` and `Mini App · Android` are one surface
 * that differs only in platform family (radii + safe inset).
 */
export type Surface = 'telegram' | 'web-mobile' | 'web-desktop';

export interface SurfaceInfo {
  surface: Surface;
  isTelegram: boolean;
  isWebMobile: boolean;
  isDesktop: boolean;
  family: 'ios' | 'material';
  /** Safe bottom inset in px — iPhone 30 / Android 14 / web-mobile 20 / desktop 8. */
  safeBottom: number;
  /** Group-card radius — 10 (iOS) / 12 (Material). */
  cardRadius: number;
  /** FAB radius — 28 (iOS) / 18 (Android). */
  fabRadius: number;
}

function detectFamily(): 'ios' | 'material' {
  const platform = getTelegramWebApp()?.platform;
  if (platform) return platform === 'ios' || platform === 'macos' ? 'ios' : 'material';
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  return /iPhone|iPad|iPod|Macintosh/.test(ua) ? 'ios' : 'material';
}

function viewportWidth(): number {
  if (typeof window === 'undefined') return DESKTOP_BREAKPOINT;
  return window.innerWidth;
}

function compute(width: number): SurfaceInfo {
  const webApp = getTelegramWebApp();
  const isTelegram = typeof webApp?.initData === 'string' && webApp.initData.length > 0;
  const family = detectFamily();
  const isDesktop = !isTelegram && width >= DESKTOP_BREAKPOINT;
  const surface: Surface = isTelegram ? 'telegram' : isDesktop ? 'web-desktop' : 'web-mobile';

  const safeBottom = isTelegram
    ? family === 'ios'
      ? SAFE_BOTTOM['telegram-ios']
      : SAFE_BOTTOM['telegram-android']
    : isDesktop
      ? SAFE_BOTTOM['web-desktop']
      : SAFE_BOTTOM['web-mobile'];

  return {
    surface,
    isTelegram,
    isWebMobile: surface === 'web-mobile',
    isDesktop,
    family,
    safeBottom,
    cardRadius: family === 'ios' ? TOKENS.card.radiusIos : TOKENS.card.radiusMaterial,
    fabRadius: family === 'ios' ? TOKENS.fab.radiusIos : TOKENS.fab.radiusMaterial,
  };
}

export function useSurface(): SurfaceInfo {
  const [width, setWidth] = useState(viewportWidth);

  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const info = useMemo(() => compute(width), [width]);

  // Surface-dependent layout constants live on the root so plain CSS can use
  // them without every component threading props.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--safe-bottom', `${info.safeBottom}px`);
    root.style.setProperty('--card-radius', `${info.cardRadius}px`);
    root.style.setProperty('--fab-radius', `${info.fabRadius}px`);
    root.style.setProperty(
      '--list-row-h',
      `${info.isDesktop ? TOKENS.listRow.minHeightDesktop : TOKENS.listRow.minHeightMobile}px`,
    );
    root.style.setProperty(
      '--item-row-h',
      `${info.isDesktop ? TOKENS.itemRow.minHeightDesktop : TOKENS.itemRow.minHeightMobile}px`,
    );
    root.dataset.surface = info.surface;
  }, [info]);

  return info;
}
